import { memo, useEffect, useMemo, useRef } from "react";
import { Handle, Position, NodeProps, useUpdateNodeInternals } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import { Gauge } from "lucide-react";
import { LoadBalancerNodeData, HandleConfig } from "../../types";
import { useNodeResizing } from '../../hooks/useNodeResizing';
import { NODE_DIMENSIONS, groupHandlesByPosition } from '../../utils/nodeHelpers';

// Helper function to calculate dynamic style for handles on a side (copied from DestinationNode)
const calculateHandleStyle = (index: number, total: number, handlePosition: Position): React.CSSProperties => {
  const calcOffset = 5;
  const percentage = (index + 1) * (100 / (total + 1));

  switch (handlePosition) {
    case Position.Top:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, top: `-${calcOffset}px` };
    case Position.Bottom:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, bottom: `-${calcOffset}px` };
    case Position.Left:
    case Position.Right:
    default:
      return { top: `calc(${percentage}% - ${calcOffset}px)` };
  }
};

function LoadBalancerNode({
  data,
  selected,
  id
}: NodeProps<LoadBalancerNodeData>) {
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeContentRef = useRef<HTMLDivElement>(null);

  // Calculate minimum height based on content
  const calculateMinHeight = useMemo(() => {
    return (width: number, headerHeight: number) => {
      let calculatedHeight = NODE_DIMENSIONS.VERTICAL_PADDING;
      calculatedHeight += headerHeight || NODE_DIMENSIONS.HEADER_CONTENT_ESTIMATED_HEIGHT;

      const showLabels = width >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD;

      if (showLabels) {
        let visibleLabelCount = 0;
        if (data.status && data.statusVisibility !== "hidden") visibleLabelCount++;
        if (data.strategy && data.strategyVisibility !== "hidden") visibleLabelCount++;
        if (data.customLabel && data.customLabelVisibility !== "hidden") visibleLabelCount++;

        if (visibleLabelCount > 0) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
        // Simple heuristic: if more than 1 label and width is tight, assume a second row for labels
        if (visibleLabelCount > 1 && width < (NODE_DIMENSIONS.MIN_WIDTH + 60)) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
      }
      return Math.min(calculatedHeight, NODE_DIMENSIONS.MAX_HEIGHT);
    };
  }, [data]);

  // Use the node resizing hook
  const {
    onResize,
    onResizeEnd,
    currentWidth,
    currentHeight,
    effectiveMinHeight
  } = useNodeResizing({
    id,
    nodeType: 'load-balancer',
    minWidth: NODE_DIMENSIONS.MIN_WIDTH,
    maxWidth: NODE_DIMENSIONS.MAX_WIDTH,
    minHeight: NODE_DIMENSIONS.MIN_HEIGHT,
    maxHeight: NODE_DIMENSIONS.MAX_HEIGHT,
    contentRef: nodeContentRef,
    calculateMinHeight
  });

  // Effect to update node internals when handles change
  useEffect(() => {
    if (Array.isArray(data.handles)) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);

  // --- Handle Rendering Logic ---
  const handles = useMemo(() => {
    const defaultLbHandles: HandleConfig[] = [
      { id: 'default-target', type: 'target', position: Position.Left, color: '#3B82F6' },
      { id: 'default-source', type: 'source', position: Position.Right, color: '#3B82F6' },
    ];
    return Array.isArray(data.handles) ? [...data.handles] : defaultLbHandles;
  }, [data.handles]);

  const handlesByPosition = useMemo(() => {
    return groupHandlesByPosition(handles);
  }, [handles]);
  // --- End Handle Rendering Logic ---

  return (
    <div
      style={{
        width: currentWidth,
        height: currentHeight,
        position: 'relative',
      }}
      className="node-wrapper"
    >
      {selected && (
        <NodeResizer
          minWidth={NODE_DIMENSIONS.MIN_WIDTH}
          maxWidth={NODE_DIMENSIONS.MAX_WIDTH}
          minHeight={effectiveMinHeight}
          maxHeight={NODE_DIMENSIONS.MAX_HEIGHT}
          isVisible={selected}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6',
            border: '1px solid white',
            borderRadius: '2px',
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}

      <div
        className={`
          w-full h-full pl-4 pr-4 py-3 rounded-lg bg-white dark:bg-gray-700 border transition-all shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark overflow-hidden
          flex flex-col justify-center items-center
            ${selected
              ? "border-indigo-500 shadow-indigo-100 outline outline-4 outline-indigo-500/20 outline-offset-0"
              : "border-gray-300 dark:border-gray-600"
            }
        `}
      >
        <div ref={nodeContentRef} className="flex items-center justify-center gap-2 node-content">
          <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
            <Gauge className="h-4 w-4" />
          </div>
          <div className="min-w-0 content-container">
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {data.label}
            </h3>
            {currentWidth >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD && (
              <div className="labels-container flex flex-wrap gap-x-1">
                {/* Environment/Status - only show if visibility is not hidden */}
                {data.status && data.status.trim() !== "" && data.statusVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-indigo-100 text-indigo-800"
                  >
                    {data.status}
                  </span>
                )}

                {/* Strategy - only show if visibility is not hidden */}
                {data.strategy && data.strategyVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-blue-100 text-blue-800"
                  >
                    {data.strategy === 'round-robin' ? 'Round Robin' :
                     data.strategy === 'least-connections' ? 'Least Connections' :
                     data.strategy === 'ip-hash' ? 'IP Hash' :
                     data.strategy === 'weighted' ? 'Weighted' : data.strategy}
                  </span>
                )}

                {/* Custom Label - only show if visibility is not hidden */}
                {data.customLabel && data.customLabelVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-yellow-100 text-yellow-800"
                  >
                    {data.customLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* --- Handle Rendering --- */}
        {handles && handles.length > 0 &&
          Object.entries(handlesByPosition).map(([_, positionHandles]) =>
            positionHandles.map((handle, index) => (
              <Handle
                key={`${handle.id}-${handle.color}`}
                id={handle.id}
                type={handle.type}
                position={handle.position as Position}
                className={`!w-3 !h-3 !border-2 !border-white dark:!border-gray-700`}
                style={{
                  ...calculateHandleStyle(index, positionHandles.length, handle.position),
                  backgroundColor: handle.color,
                }}
              />
            ))
          )
        }
        {/* --- End Handle Rendering --- */}
      </div>
    </div>
  );
}

export default memo(LoadBalancerNode);
